import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import ShellLayout from "../components/shell/ShellLayout";
import PortalShellLayout from "../components/shell/PortalShellLayout";
import { liveGuardAllows } from "../components/shell/liveRouteGuards";
import { PortalSessionProvider } from "../context/PortalSessionContext";
import AdminLayout from "../components/admin/AdminLayout";
import AuthLayout from "../layouts/AuthLayout";
import CustomerLayout from "../layouts/CustomerLayout";
import LoadingSpinner from "../components/feedback/LoadingSpinner";
import AdminErrorBoundary from "../components/common/AdminErrorBoundary";
import { useAuthContext } from "../context/AuthContext";

// ── Auth pages ──
const Login = lazy(() => import("../pages/auth/Login"));
const AuthCallback = lazy(() => import("../pages/auth/AuthCallback"));

// ── Main pages (Figma-designed) ──
const Dashboard = lazy(() => import("../pages/Dashboard"));
const ListManagement = lazy(() => import("../pages/ListManagement"));
const OutcomeAnalytics = lazy(() => import("../pages/OutcomeAnalytics"));
const EnrichmentPlans = lazy(() => import("../pages/EnrichmentPlans"));
const DataGroups = lazy(() => import("../pages/DataGroups"));
const ContactStatus = lazy(() => import("../pages/ContactStatus"));
const ExportImport = lazy(() => import("../pages/ExportImport"));
const Connections = lazy(() => import("../pages/Connections"));
const HeManSearch = lazy(() => import("../pages/HeManSearch"));
const Settings = lazy(() => import("../pages/Settings"));
const Profile = lazy(() => import("../pages/Profile"));
const HelpCenter = lazy(() => import("../pages/HelpCenter"));
const ComponentShowcase = lazy(() => import("../pages/ComponentShowcase"));
const NotFound = lazy(() => import("../pages/NotFound"));

// ── Admin pages ──
const TenantManagement = lazy(() => import("../pages/admin/TenantManagement"));
const UserManagement = lazy(() => import("../pages/admin/UserManagement"));
const UserProfile = lazy(() => import("../pages/admin/UserProfile"));
const SystemSettings = lazy(() => import("../pages/admin/SystemSettings"));
const WhiteLabelingControls = lazy(
  () => import("../pages/admin/WhiteLabelingControls"),
);
const TenantMigrationTools = lazy(
  () => import("../pages/admin/TenantMigrationTools"),
);
const AuditLogging = lazy(() => import("../pages/admin/AuditLogging"));
const BillingSubscriptionManagement = lazy(
  () => import("../pages/admin/BillingSubscriptionManagement"),
);
const AccessControlManagement = lazy(
  () => import("../pages/admin/AccessControlManagement"),
);
const NotificationSettings = lazy(
  () => import("../pages/admin/NotificationSettings"),
);
const ApiUsageMonitoring = lazy(
  () => import("../pages/admin/ApiUsageMonitoring"),
);
const ThreatDetection = lazy(() => import("../pages/admin/ThreatDetection"));
const EnhancedMonitoring = lazy(
  () => import("../pages/admin/EnhancedMonitoring"),
);
const ZeroTrustAuth = lazy(() => import("../pages/admin/ZeroTrustAuth"));

// ── Tenant Lead Portal pages (#663 / epic #669) ──
const PortalHome = lazy(() => import("../pages/portal/PortalHome"));
const PortalLeadRequests = lazy(() => import("../pages/portal/LeadRequests"));
const PortalDeliveries = lazy(() => import("../pages/portal/Deliveries"));
const PortalAreaPlaceholder = lazy(
  () => import("../pages/portal/PortalAreaPlaceholder"),
);

// ── Customer pages ──
const CustomerDashboard = lazy(() => import("../pages/customer/Dashboard"));
const CustomerUsage = lazy(() => import("../pages/customer/Usage"));
const CustomerBilling = lazy(() => import("../pages/customer/Billing"));
const CustomerApiKeys = lazy(() => import("../pages/customer/ApiKeys"));

// ── Guards ──

const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuthContext();

  if (isLoading) {
    return <PageLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/**
 * Route-level role gate. The roles are NOT written here — they come from
 * `src/components/shell/liveRouteGuards`, the same table the shell nav asks
 * before it advertises an entry, so nav visibility and route enforcement
 * cannot drift (the old inline `requiredRoles` arrays were exactly that
 * drift: nav offered `viewer` a List Management link the guard refused).
 *
 * `path` is the route's own mounted path; the table resolves it by
 * longest-prefix, so /lists/analytics inherits /lists.
 */
const RoleGuard = ({
  children,
  path,
}: {
  children: React.ReactNode;
  path: string;
}) => {
  const { user } = useAuthContext();

  if (!liveGuardAllows(user?.role, path)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const RootRedirect = () => {
  const { user, isLoading } = useAuthContext();

  if (isLoading) return <PageLoading />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to="/dashboard" replace />;
};

const PageLoading = () => (
  <div className="flex items-center justify-center h-full min-h-screen">
    <LoadingSpinner size="large" />
  </div>
);

const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Root redirect */}
        <Route path="/" element={<RootRedirect />} />

        {/* Auth routes */}
        <Route
          path="/login"
          element={
            <AuthLayout>
              <Login />
            </AuthLayout>
          }
        />
        {/* NU Core SSO redirect URI (#590) — registered for nudata-client. */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* ── Main layout: NuShell side-rail chrome (#601 S3, brief 00).
              Supersedes MainLayout (SidebarMenu + TopHeader) for this tree;
              guards are unchanged — visual adoption only. ── */}
        <Route
          element={
            <AuthGuard>
              <ShellLayout />
            </AuthGuard>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          {/* #601 W2 (briefs 03+04): /lists and its analytics sub-page are
              distinct screens. Same guard on both — brief 04's RBAC row is
              "same as Lists"; the full 15-role matrix rewrite is a separate
              routing/guards task (recon R1 §3.9). */}
          <Route
            path="/lists"
            element={
              <RoleGuard path="/lists">
                <ListManagement />
              </RoleGuard>
            }
          />
          <Route
            path="/lists/analytics"
            element={
              <RoleGuard path="/lists/analytics">
                <OutcomeAnalytics />
              </RoleGuard>
            }
          />
          <Route
            path="/enrichment/*"
            element={
              <RoleGuard path="/enrichment">
                <EnrichmentPlans />
              </RoleGuard>
            }
          />
          <Route
            path="/data-groups/*"
            element={
              <RoleGuard path="/data-groups">
                <DataGroups />
              </RoleGuard>
            }
          />
          {/* #601 W2-5 (brief 15): Contact Status ledger. ROLE-MATRIX row 11
              grants Suppression to owner/admin/tenant_admin only — of the
              live session roles only `admin` falls in that set, so the guard
              is admin-only (the matrix's live wiring stays a separate task,
              recon R1 §3.9). The #637 status mutation is additionally
              admin-gated server-side (requireAdmin). */}
          <Route
            path="/suppression/*"
            element={
              <RoleGuard path="/suppression">
                <ContactStatus />
              </RoleGuard>
            }
          />
          <Route
            path="/export/*"
            element={
              <RoleGuard path="/export">
                <ExportImport />
              </RoleGuard>
            }
          />
          <Route
            path="/connections/*"
            element={
              <RoleGuard path="/connections">
                <Connections />
              </RoleGuard>
            }
          />
          <Route path="/search" element={<HeManSearch />} />
          {/* #601 W3-1 (brief 12): tabbed account surfaces. ROLE-MATRIX rows
              17 (Account Settings) + 22 (Profile) grant ALL roles, so these
              mount behind AuthGuard only — no RoleGuard (the matrix's live
              wiring stays a separate task, #641). */}
          <Route path="/settings" element={<Settings />} />
          <Route path="/profile" element={<Profile />} />
          {/* #601 W3 (brief 13): Help Center. ROLE-MATRIX row 19 grants ALL
              canonical roles, so no RoleGuard — AuthGuard alone. Replaces the
              in-shell 404 the shell nav's Help links previously landed on. */}
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/showcase" element={<ComponentShowcase />} />
          {/* In-shell catch-all: unmatched paths for an authenticated session
              (incl. not-yet-ported nav targets like /help) render
              NotFound INSIDE the shell chrome instead of a bare page. Explicit
              routes below (admin/customer/…) outrank "*" so they are
              unaffected; unauthenticated visitors hit AuthGuard → /login. */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* ── Tenant Lead Portal (#663, epic #669) ──
              A SECTION of the same shell (PortalShellLayout wraps the same
              NuShell chrome), not a second app. Access is a per-tenant PAGE
              GRANT, so there is no RoleGuard here: PortalShellLayout renders
              the designed permission-denied state for an ungranted area, and
              every underlying read is refused server-side by
              requirePortalGrant — hidden nav is never the boundary.

              Prefix is /lead-portal, distinct from the API-reseller portal
              surface the epic warns not to conflate.

              Requests and Deliveries mount their REAL slice-4 screens (#664,
              landed in #692 — the pages shipped unrouted, so the placeholder
              was still rendering over them). Results, Returns and Complaints
              keep the honest placeholder until #665 / #666 land; those slices
              swap the element here. ── */}
        <Route
          path="/lead-portal"
          element={
            <AuthGuard>
              <PortalSessionProvider>
                <PortalShellLayout />
              </PortalSessionProvider>
            </AuthGuard>
          }
        >
          <Route index element={<PortalHome />} />
          <Route path="requests" element={<PortalLeadRequests />} />
          <Route path="deliveries" element={<PortalDeliveries />} />
          <Route
            path="reports"
            element={
              <PortalAreaPlaceholder
                area="Results Reports"
                teach="Each month you return dispositions and conversions for the leads delivered to you, keyed to your configured month-end calendar."
                slice="slice #665"
              />
            }
          />
          <Route
            path="returns"
            element={
              <PortalAreaPlaceholder
                area="Opt-Out Returns"
                teach="Records that hit an opt-out register can be returned inside your return window; verified hits are replaced one-for-one at no charge."
                slice="slice #665"
              />
            }
          />
          <Route
            path="complaints"
            element={
              <PortalAreaPlaceholder
                area="Complaints"
                teach="Complaints logged against a delivered lead run an acknowledgement clock, then suppression and cessation, then resolution — with the consent-event log alongside."
                slice="slice #666"
              />
            }
          />
          {/* Any other /lead-portal path is not a granted area — the layout's
              denied state answers it rather than a bare 404. */}
          <Route path="*" element={null} />
        </Route>

        {/* ── Admin routes ── */}
        <Route
          path="/admin"
          element={
            <AuthGuard>
              <RoleGuard path="/admin">
                <AdminErrorBoundary
                  fallback={
                    <div className="flex items-center justify-center min-h-screen">
                      <div className="text-center">
                        <h2 className="text-xl font-semibold mb-2">
                          Admin Portal Error
                        </h2>
                        <p className="text-figma-text-light">
                          Please refresh to continue.
                        </p>
                      </div>
                    </div>
                  }
                >
                  <AdminLayout />
                </AdminErrorBoundary>
              </RoleGuard>
            </AuthGuard>
          }
        >
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route
            path="dashboard"
            element={
              <div className="p-6">
                <h1 className="text-h1 font-medium text-figma-text">
                  Admin Dashboard
                </h1>
              </div>
            }
          />
          <Route path="tenants" element={<TenantManagement />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="profile" element={<UserProfile />} />
          <Route path="system" element={<SystemSettings />} />
          <Route path="settings" element={<SystemSettings />} />
          <Route path="white-labeling" element={<WhiteLabelingControls />} />
          <Route path="tenant-migration" element={<TenantMigrationTools />} />
          <Route path="audit" element={<AuditLogging />} />
          <Route path="audit-logging" element={<AuditLogging />} />
          <Route path="billing" element={<BillingSubscriptionManagement />} />
          <Route path="access-control" element={<AccessControlManagement />} />
          <Route path="notifications" element={<NotificationSettings />} />
          <Route path="api-keys" element={<ApiUsageMonitoring />} />
          <Route path="api-monitoring" element={<ApiUsageMonitoring />} />
          <Route path="monitoring" element={<EnhancedMonitoring />} />
          <Route
            path="cache"
            element={
              <div className="p-6">
                <h1 className="text-h1 font-medium text-figma-text">
                  Cache Management
                </h1>
              </div>
            }
          />
          <Route
            path="analytics"
            element={
              <div className="p-6">
                <h1 className="text-h1 font-medium text-figma-text">
                  Analytics
                </h1>
              </div>
            }
          />
          <Route path="threat-detection" element={<ThreatDetection />} />
          <Route path="enhanced-monitoring" element={<EnhancedMonitoring />} />
          <Route path="zero-trust-auth" element={<ZeroTrustAuth />} />
        </Route>

        {/* ── Customer routes ── */}
        <Route
          path="/customer"
          element={
            <AuthGuard>
              <RoleGuard path="/customer">
                <CustomerLayout />
              </RoleGuard>
            </AuthGuard>
          }
        >
          <Route
            index
            element={<Navigate to="/customer/dashboard" replace />}
          />
          <Route path="dashboard" element={<CustomerDashboard />} />
          <Route path="usage" element={<CustomerUsage />} />
          <Route path="billing" element={<CustomerBilling />} />
          <Route path="api-keys" element={<CustomerApiKeys />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
